import { useCallback, useEffect, useState } from "react"
import { Button, Card, Form, FormControl, InputGroup, Modal, Stack } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useApi } from "../api"
import { AppHeader } from "../components/AppHeader"
import { ErrorDialog } from "../components/ErrorDialog"
import { SuccessDialog } from "../components/SuccessDialog"
import { ICargo } from "../interfaces"

export const NftPreSalePage = () => {
  const api = useApi('SAI')
  const { t } = useTranslation()
  const navi = useNavigate()
  const [isMounted, setIsMounted] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [showEditModel, setShowEditModel] = useState(false)
  const [showGiftToModel, setShowGiftToModel] = useState(false)
  const [nftAssets, setNftAssets] = useState([])
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [selectedSellingPrice, setSelectedSellingPrice] = useState(0)
  const [selectedSellingStatus, setSelectedSellingStatus] = useState('P')
  const [releaseToEthereum, setReleaseToEthereum] = useState(false)
  const [otp, setOtp] = useState<string | null>(null)
  const [simpleCode, setSimpleCode] = useState<string | null>(null)

  const fetchNftAssets = useCallback(() => {
    api.getUserNftAssets((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setNftAssets(cargo.info)
      }
    })
  }, [api])

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true)
      fetchNftAssets()
    }
  }, [api, fetchNftAssets, isMounted])

  const handleClose = () => {
    setShowEditModel(false)
    setShowGiftToModel(false)
  }

  return (
    <>
      <div className="content colorbk" style={{ backgroundImage: `url('https://ai.supercoin.network/img/bgr.jpg')`, backgroundSize: "100% 100%" }}>
        <AppHeader title="我的NFT"></AppHeader>
        <div className="m-86 p-4 p-sm-5 text-right" style={{ "background": "#fff" }}>
          <Button variant="outline-success" className="mr-3" onClick={() => {
            navi('/app/nft-explore')
          }}>{t('exploreNft')}</Button>
          <Button variant="outline-success" onClick={() => {
            navi('/app/nft-mint')
          }}>{t('mintNft')}</Button>
        </div>
        <div className="m-86 p-2 p-sm-5" style={{ "background": "#fff" }}>
          {
            nftAssets.filter((asset) => asset['image']).map((asset, index) => {
              const image: string = asset['image'];
              const imageUrl = image.indexOf('http') > -1 ? image : `https://file.sup.me.uk/nft/${image}`
              return (
                <Card key={index} border='light' className='border shadow mx-3 mb-5 rounded-md'>
                  <Card.Img variant="top" src={imageUrl} />
                  <Card.Body>
                    <Card.Title className="mb-0 text-rose-500">{asset['name']}</Card.Title>
                    <div>
                      <div className="text-gray-400 text-xs mb-3">
                        <Stack direction="horizontal" gap={1}>
                          <div className="text-sm">{`${new Intl.DateTimeFormat('ko-KR').format(new Date(asset['timelog']))}`}</div>
                          <div className="ms-auto text-sm">{t('buyingPrice')}</div>
                          <div className="vr" />
                          <div className="text-sm">{new Intl.NumberFormat('en').format(asset['buyingPrice'])}</div>
                        </Stack>
                      </div>
                      <hr />
                      <div className="text-gray-700 text-sm">{asset['description']}</div>
                      <div className="text-gray-400 text-xs mt-3 bg-slate-200 rounded-lg p-3 text-center">{asset['nftId']}</div>
                      <div className="text-right mt-3 text-purple-800 cursor-pointer" onClick={() => {
                        navi(`/web/asset/detail/${asset['nftId']}`)
                      }}>{t('detail')}</div>
                    </div>
                  </Card.Body>
                  {asset['chainType'] === 'Star River' &&
                    <Card.Footer>
                      <Stack direction="horizontal" gap={1}>
                        <div className="text-sm">{t('sellingPrice')}</div>
                        <div className="ms-auto text-sm">{new Intl.NumberFormat('en').format(asset['sellingPrice'])}</div>
                        <div className="vr" />
                        <div className="bg-light border text-sm p-1 px-3 rounded cursor-pointer" onClick={() => {
                          setSelectedAsset(asset)
                          setSelectedSellingPrice(asset['sellingPrice'])
                          setSelectedSellingStatus(asset['status'])
                          setShowEditModel(true)
                        }}>{t('edit')}</div>
                        <div className="bg-light border text-sm p-1 px-3 rounded cursor-pointer" onClick={() => {
                          setSelectedAsset(asset)
                          setShowGiftToModel(true)
                        }}>{t('giftTo')}</div>
                      </Stack>
                    </Card.Footer>
                  }
                </Card>
              )
            })
          }
          <Modal show={showEditModel} onHide={handleClose} aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header closeButton>
              <Modal.Title>{selectedAsset != null && selectedAsset['name']}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Label>{t('release')}</Form.Label>
                <Form.Check
                  type="switch"
                  label="Start River"
                  checked={selectedSellingStatus === 'S' ? true : false}
                  onChange={(e) => {
                    setSelectedSellingStatus(e.target.checked ? 'S' : 'P')
                    setReleaseToEthereum(false)
                  }}
                />
                <Form.Check
                  type="switch"
                  label="Ethereum"
                  checked={releaseToEthereum}
                  onChange={(e) => {
                    setReleaseToEthereum(e.target.checked)
                    setSelectedSellingStatus('P')
                  }}
                />
                {selectedSellingStatus === 'S' &&
                  <Form.Group className="mt-4 mb-3">
                    <Form.Label>{t('editSellingPrice')}</Form.Label>
                    <Form.Control
                      className="text-right"
                      type="number"
                      value={selectedSellingPrice}
                      onChange={(e) => {
                        setSelectedSellingPrice(Number(e.target.value))
                      }}
                      autoFocus
                    />
                  </Form.Group>
                }
              </Form>
            </Modal.Body>
            <Modal.Footer>
              {
                selectedAsset && selectedAsset['chainType'] === 'Star River' && !releaseToEthereum &&
                <Button variant="primary" disabled={isLoading} onClick={() => {
                  if (selectedAsset) {
                    setLoading(true)
                    api.updateNftSellingPrice(selectedAsset['nftId'], selectedSellingStatus, selectedSellingPrice, (cargo: ICargo) => {
                      setLoading(false)
                      if (cargo.returnCode === '000000') {
                        fetchNftAssets()
                        handleClose()
                      }
                    })
                  }
                }}>
                  {isLoading ? t('processing') : t('saveChange')}
                </Button>
              }
              {
                releaseToEthereum &&
                <Button variant="primary" disabled={isLoading} onClick={() => {
                  if (selectedAsset) {

                  }
                }}>
                  {isLoading ? t('processing') : t('releaseToEthereum')}
                </Button>
              }
            </Modal.Footer>
          </Modal>
          <Modal show={showGiftToModel} onHide={handleClose} aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('giftNft')} {selectedAsset != null && selectedAsset['name']}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mt-3 mb-3">
                <Form.Label>{t('simpleCode')}</Form.Label>
                <Form.Control
                  className="text-left"
                  type="string"
                  onChange={(e) => {
                    setSimpleCode(e.target.value)
                  }}
                  autoFocus
                />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label>{t('oneTimePasscode')}</Form.Label>
                <InputGroup>
                  <FormControl
                    type="password"
                    onChange={(e) => {
                      setOtp(e.target.value)
                    }}
                    aria-label={t('pleaseInputOtp')}
                  />
                  <Button variant="outline-danger" disabled={isLoading} onClick={() => {
                    setLoading(true)
                    api.sendOtp((cargo: ICargo) => {
                      setLoading(false)
                      if (cargo.returnCode === '000000') {
                        SuccessDialog({ title: t('otpSentSuccess') })
                      } else {
                        switch (cargo.returnCode) {
                          case '100003':
                            ErrorDialog({ title: t(`errorCode.${cargo.returnCode}`) })
                            window.location.href = '/login.html'
                            break;
                          default:
                            ErrorDialog({ title: t('otpSentFailure') })
                        }
                      }
                    })
                  }}>{t('sendOtp')}</Button>
                </InputGroup>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" disabled={isLoading} onClick={() => {
                if (otp === null) {
                  ErrorDialog({ title: t('pleaseInputOtp') })
                  return
                }
                if (simpleCode === null) {
                  ErrorDialog({ title: t('pleaseInputSimpleCode') })
                  return
                }
                if (selectedAsset) {
                  setLoading(true)
                  api.giftNft(selectedAsset['nftId'], otp, simpleCode, (cargo: ICargo) => {
                    setLoading(false)
                    if (cargo.returnCode === '000000') {
                      fetchNftAssets()
                      handleClose()
                    }
                  })
                }
              }}>
                {isLoading ? t('processing') : t('giftTo')}
              </Button>
            </Modal.Footer>
          </Modal>
          <div className="p-5"></div>
        </div>
      </div>
    </>
  )
}